import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, RefreshCw, LogOut, Euro, ShoppingBag, Clock, Package, Check, ChevronDown, MapPin, User, Trash2 } from "lucide-react";
import SpideyLogo from "@/components/SpideyLogo";
import { eur } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
    paid: { label: "Payée", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
    pending: { label: "En attente", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-600/40" },
    failed: { label: "Échouée", cls: "bg-red-500/15 text-red-400 border-red-500/40" },
    expired: { label: "Expirée", cls: "bg-zinc-500/15 text-zinc-500 border-zinc-700/40" },
    refunded: { label: "Remboursée", cls: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
};

function formatError(detail) {
    if (detail == null) return "Une erreur est survenue.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function AdminOrders() {
    const [user, setUser] = useState(undefined);
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const loadOrders = async () => {
        setLoadingOrders(true);
        try {
            const res = await fetch(`${API}/orders`, { credentials: "include" });
            if (res.ok) setOrders(await res.json());
        } finally {
            setLoadingOrders(false);
        }
    };

    const markShipped = async (sessionId) => {
        await fetch(`${API}/orders/${sessionId}/ship`, { method: "POST", credentials: "include" });
        loadOrders();
    };

    const deleteOrder = async (sessionId) => {
        await fetch(`${API}/orders/${sessionId}`, { method: "DELETE", credentials: "include" });
        setConfirmDelete(null);
        setExpanded(null);
        loadOrders();
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/auth/me`, { credentials: "include" });
                if (res.ok) {
                    setUser(await res.json());
                    loadOrders();
                } else {
                    setUser(null);
                }
            } catch {
                setUser(null);
            }
        })();
    }, []);

    const login = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(formatError(data.detail));
                return;
            }
            setUser(data);
            loadOrders();
        } catch {
            setError("Impossible de se connecter. Réessaie.");
        } finally {
            setSubmitting(false);
        }
    };

    const logout = async () => {
        await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
        setUser(null);
        setOrders([]);
    };

    const paid = orders.filter((o) => o.payment_status === "paid");
    const revenue = paid.reduce((sum, o) => sum + o.amount / 100, 0);

    if (user === undefined) {
        return (
            <div className="min-h-screen bg-spidey-bg flex items-center justify-center">
                <Loader2 className="text-spidey-red animate-spin" size={32} />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-spidey-bg flex items-center justify-center px-5 noise relative">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-sm bg-spidey-card border border-zinc-800 rounded-3xl p-8"
                >
                    <div className="flex justify-center mb-6">
                        <SpideyLogo size={34} />
                    </div>
                    <h1 className="font-display uppercase text-2xl text-white text-center">
                        Espace vendeur
                    </h1>
                    <p className="mt-2 text-center text-sm text-zinc-500 flex items-center justify-center gap-1.5">
                        <Lock size={13} className="text-spidey-red" />
                        Accès réservé — suivi des ventes
                    </p>
                    <form onSubmit={login} className="mt-7 space-y-4">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email vendeur"
                            className="w-full bg-black/60 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-spidey-red"
                            data-testid="admin-email-input"
                        />
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Mot de passe"
                            className="w-full bg-black/60 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-spidey-red"
                            data-testid="admin-password-input"
                        />
                        {error && (
                            <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="admin-login-error">
                                {error}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full flex items-center justify-center gap-2 bg-spidey-red hover:bg-spidey-neon disabled:opacity-70 text-white font-bold uppercase tracking-wider text-sm py-3.5 rounded-full transition-all hover:shadow-[0_0_30px_rgba(230,0,38,0.5)]"
                            data-testid="admin-login-submit-button"
                        >
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : "Se connecter"}
                        </button>
                    </form>
                    <Link to="/" className="block mt-5 text-center text-xs text-zinc-600 hover:text-spidey-neon transition-colors">
                        ← Retour à la boutique
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-spidey-bg noise relative">
            <header className="border-b border-zinc-900 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
                    <Link to="/">
                        <SpideyLogo />
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={loadOrders}
                            className="flex items-center gap-2 text-zinc-400 hover:text-spidey-neon text-xs font-mono-brand uppercase tracking-wider border border-zinc-800 hover:border-spidey-red/60 rounded-full px-4 py-2 transition-all"
                            data-testid="orders-refresh-button"
                        >
                            <RefreshCw size={13} className={loadingOrders ? "animate-spin" : ""} />
                            Rafraîchir
                        </button>
                        <button
                            onClick={logout}
                            className="flex items-center gap-2 text-zinc-400 hover:text-white text-xs font-mono-brand uppercase tracking-wider border border-zinc-800 rounded-full px-4 py-2 transition-all"
                            data-testid="admin-logout-button"
                        >
                            <LogOut size={13} />
                            Sortir
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-10" data-testid="admin-orders-container">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <p className="text-xs font-mono-brand uppercase tracking-[0.3em] text-spidey-neon">
                        Espace vendeur
                    </p>
                    <h1 className="mt-2 font-display uppercase text-3xl sm:text-4xl text-white">
                        Suivi des ventes
                    </h1>

                    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { icon: Euro, label: "Chiffre d'affaires payé", value: eur(revenue) },
                            { icon: ShoppingBag, label: "Commandes payées", value: paid.length },
                            { icon: Package, label: "À expédier", value: paid.filter((o) => !o.shipped).length },
                            { icon: Clock, label: "En attente de paiement", value: orders.filter((o) => o.payment_status === "pending").length },
                        ].map((s) => (
                            <div key={s.label} className="bg-spidey-card border border-zinc-800/80 rounded-2xl p-5">
                                <s.icon size={20} className="text-spidey-red" />
                                <p className="mt-3 font-display text-2xl text-white">{s.value}</p>
                                <p className="mt-1 text-[11px] font-mono-brand uppercase tracking-[0.18em] text-zinc-500">
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 bg-spidey-card border border-zinc-800/80 rounded-2xl overflow-hidden">
                        <div className="hidden md:grid grid-cols-[1fr_1.3fr_0.7fr_0.7fr_0.8fr_1fr_auto] gap-4 px-6 py-4 border-b border-zinc-800/80 text-[10px] font-mono-brand uppercase tracking-[0.2em] text-zinc-500">
                            <span>Date</span>
                            <span>Produit</span>
                            <span>Taille</span>
                            <span>Montant</span>
                            <span>Statut</span>
                            <span>Expédition</span>
                            <span />
                        </div>
                        <p className="px-6 pt-3 text-[10px] font-mono-brand uppercase tracking-[0.18em] text-zinc-600">
                            Clique sur une commande pour voir le client et l'adresse de livraison
                        </p>
                        {orders.length === 0 && !loadingOrders && (
                            <p className="px-6 py-14 text-center text-sm text-zinc-500">
                                Aucune commande pour l'instant. Les paiements Stripe apparaîtront ici en temps réel.
                            </p>
                        )}
                        {orders.map((o, i) => {
                            const s = STATUS_LABELS[o.payment_status] || STATUS_LABELS.pending;
                            const isOpen = expanded === o.session_id;
                            const toggle = () => setExpanded(isOpen ? null : o.session_id);
                            return (
                                <div key={o.session_id} className="border-b border-zinc-900/60 last:border-0">
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={toggle}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
                                        className="grid md:grid-cols-[1fr_1.3fr_0.7fr_0.7fr_0.8fr_1fr_auto] gap-1 md:gap-4 px-6 py-4 text-sm items-center cursor-pointer hover:bg-white/[0.03] transition-colors"
                                        data-testid={`order-row-${i}`}
                                    >
                                        <span className="text-zinc-500 text-xs md:text-sm">
                                            {new Date(o.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                                        </span>
                                        <span className="text-white font-medium">
                                            {o.items && o.items.length > 1 ? `${o.items.length} articles` : o.product_name}
                                            {o.items && o.items.length > 1 && (
                                                <span className="block text-[11px] font-normal text-zinc-500">
                                                    {o.items.map((it) => it.name).join(" + ")}
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-zinc-400 font-mono-brand text-xs">
                                            {o.items && o.items.length > 1 ? "—" : o.size || "—"}
                                        </span>
                                        <span className="text-white font-bold">{eur(o.amount / 100)}</span>
                                        <span>
                                            <span className={`inline-block text-[10px] font-mono-brand uppercase tracking-wider border rounded-full px-2.5 py-1 ${s.cls}`}>
                                                {s.label}
                                            </span>
                                        </span>
                                        <span>
                                            {o.payment_status === "paid" ? (
                                                o.shipped ? (
                                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-brand uppercase tracking-wider border border-emerald-500/40 bg-emerald-500/15 text-emerald-400 rounded-full px-2.5 py-1">
                                                        <Check size={11} />
                                                        Expédiée
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); markShipped(o.session_id); }}
                                                        className="text-[10px] font-mono-brand uppercase tracking-wider border border-spidey-red/60 text-spidey-neon hover:bg-spidey-red hover:text-white rounded-full px-3 py-1.5 transition-all"
                                                        data-testid={`ship-button-${i}`}
                                                    >
                                                        Marquer expédiée
                                                    </button>
                                                )
                                            ) : (
                                                <span className="text-zinc-700 text-xs">—</span>
                                            )}
                                        </span>
                                        <span className="flex items-center justify-end gap-2">
                                            {confirmDelete === o.session_id ? (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); deleteOrder(o.session_id); }}
                                                    className="text-[10px] font-mono-brand uppercase tracking-wider border border-red-500/70 bg-red-500/15 text-red-400 hover:bg-red-500 hover:text-white rounded-full px-3 py-1.5 transition-all whitespace-nowrap"
                                                    data-testid={`delete-confirm-${i}`}
                                                >
                                                    Supprimer ?
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setConfirmDelete(o.session_id);
                                                        setTimeout(() => setConfirmDelete((cur) => (cur === o.session_id ? null : cur)), 3000);
                                                    }}
                                                    className="text-zinc-600 hover:text-red-400 transition-colors p-1"
                                                    title="Supprimer cette commande"
                                                    data-testid={`delete-button-${i}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                            <ChevronDown
                                                size={15}
                                                className={`text-zinc-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                                            />
                                        </span>
                                    </div>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.25 }}
                                            className="mx-6 mb-4 rounded-xl border border-zinc-800/80 bg-black/40 p-4"
                                            data-testid={`order-details-${i}`}
                                        >
                                            {o.items && o.items.length > 1 && (
                                                <div className="mb-4" data-testid={`order-items-${i}`}>
                                                    <p className="flex items-center gap-1.5 text-[10px] font-mono-brand uppercase tracking-[0.2em] text-zinc-500">
                                                        <Package size={11} className="text-spidey-red" />
                                                        Articles
                                                    </p>
                                                    <ul className="mt-2 space-y-1">
                                                        {o.items.map((it, k) => (
                                                            <li key={k} className="text-xs text-zinc-300">
                                                                {it.name} — {it.size}
                                                                {it.quantity > 1 ? ` ×${it.quantity}` : ""}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <div>
                                                    <p className="flex items-center gap-1.5 text-[10px] font-mono-brand uppercase tracking-[0.2em] text-zinc-500">
                                                        <User size={11} className="text-spidey-red" />
                                                        Client
                                                    </p>
                                                    <p className="mt-2 text-sm text-white font-medium" data-testid={`customer-name-${i}`}>
                                                        {o.customer_name || "—"}
                                                    </p>
                                                    <p className="text-xs text-zinc-400" data-testid={`customer-email-${i}`}>
                                                        {o.customer_email || "—"}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="flex items-center gap-1.5 text-[10px] font-mono-brand uppercase tracking-[0.2em] text-zinc-500">
                                                        <MapPin size={11} className="text-spidey-red" />
                                                        Adresse de livraison
                                                    </p>
                                                    {o.shipping_address ? (
                                                        <>
                                                            <p className="mt-2 text-sm text-white font-medium" data-testid={`shipping-name-${i}`}>
                                                                {o.shipping_name || o.customer_name || "—"}
                                                            </p>
                                                            <p className="text-xs text-zinc-400 leading-relaxed" data-testid={`shipping-address-${i}`}>
                                                                {o.shipping_address}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <p className="mt-2 text-xs text-zinc-500" data-testid={`shipping-address-${i}`}>
                                                            Adresse non enregistrée pour cette commande
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {o.shipping_fee != null && o.payment_status === "paid" && (
                                                <p className="mt-3 text-[11px] text-zinc-500" data-testid={`shipping-fee-${i}`}>
                                                    Livraison : {o.shipping_fee === 0 ? "offerte" : eur(o.shipping_fee / 100)}
                                                    {" — montant total encaissé : "}{eur(o.amount / 100)}
                                                </p>
                                            )}
                                        </motion.div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </motion.div>
            </main>
        </div>
    );
}
